/**
 * Syncs drawer size and color buttons with the main PDP variant picker.
 * - Size buttons sync with `fieldset.variant-size-option` in variant-main-picker
 * - Color buttons sync with radio inputs in swatches-variant-picker-component
 */

function findMainSizeFieldset(productId) {
  const picker =
    document.querySelector(
      `variant-picker[data-product-id="${productId}"][data-template-product-match="true"]`
    ) || document.querySelector(`variant-picker[data-product-id="${productId}"]`);
  return picker?.querySelector('fieldset.variant-size-option') ?? null;
}

function findMainColorInputs(productId) {
  // Look for color inputs in the variant-picker component
  const picker = document.querySelector(
    `variant-picker[data-product-id="${productId}"][data-template-product-match="true"]`
  ) || document.querySelector(`variant-picker[data-product-id="${productId}"]`);

  if (!picker) return null;

  // Find the color fieldset (variant-option--swatches)
  const colorFieldset = picker.querySelector('fieldset.variant-option--swatches');
  return colorFieldset?.querySelectorAll('input[type="radio"]') ?? null;
}

function syncDrawerFromMain(productId) {
  const fieldset = findMainSizeFieldset(productId);
  const containers = document.querySelectorAll(
    `[data-drawer-variant-size-selector][data-product-id="${String(productId)}"]`
  );
  if (!fieldset || containers.length === 0) return;

  const checked = fieldset.querySelector('input[type="radio"]:checked');
  const value = checked?.value ?? '';

  containers.forEach((container) => {
    const drawerButtons = container.querySelectorAll('.drawer-variant-size-selector__btn');
    const mainInputs = fieldset.querySelectorAll('input[type="radio"]');

    drawerButtons.forEach((btn) => {
      const sizeValue = btn.dataset.sizeValue;
      const match = sizeValue === value;

      // Find corresponding input in main fieldset
      const mainInput = Array.from(mainInputs).find(
        (input) => input.value === sizeValue
      );

      // Update selected state
      btn.classList.toggle('is-selected', match);
      btn.setAttribute('aria-pressed', match ? 'true' : 'false');

      // Sync disabled/unavailable state from main PDP
      if (mainInput) {
        const isDisabled = mainInput.disabled || mainInput.getAttribute('aria-disabled') === 'true';
        btn.disabled = isDisabled;
        btn.classList.toggle('is-unavailable', isDisabled);
        btn.setAttribute('aria-disabled', isDisabled ? 'true' : 'false');

        // Show/hide strikethrough SVG based on disabled state
        const svg = btn.querySelector('svg');
        if (svg) {
          svg.style.display = isDisabled ? 'block' : 'none';
        }
      }
    });
  });
}

function findDrawerVariantData(productId) {
  const script = document.querySelector(
    `.details-care-drawer__variant-data[data-product-id="${productId}"]`
  );
  if (!script) return null;
  try {
    return JSON.parse(script.textContent);
  } catch (e) {
    return null;
  }
}

// Swaps the "Product details" drawer image to the selected color, mirroring
// how the "Shop the look" detail panel does it. Prefers the gallery image
// explicitly tagged "FULL_WIDTH" for that color (data.colorImages, built in
// snippets/product-details-care-drawer-content.liquid); falls back to the
// variant's native featured image when no such tagged image exists.
function syncDrawerProductImage(productId, colorValue) {
  const data = findDrawerVariantData(productId);
  if (!data || typeof data.colorOptionIndex !== 'number') return;

  const target = (colorValue || '').toLowerCase().trim();
  if (!target) return;

  let image = data.colorImages && data.colorImages[target];

  if (!image && Array.isArray(data.variants)) {
    const match = data.variants.find(
      (variant) =>
        variant.options &&
        typeof variant.options[data.colorOptionIndex] === 'string' &&
        variant.options[data.colorOptionIndex].toLowerCase().trim() === target
    );
    image = match && match.featuredImage;
  }
  if (!image) return;

  document
    .querySelectorAll(`.details-care-drawer__product-image-wrap[data-product-id="${productId}"] img`)
    .forEach((img) => {
      img.src = image;
      img.srcset = '';
    });
}

function syncDrawerColorFromMain(productId) {
  const inputs = findMainColorInputs(productId);
  const containers = document.querySelectorAll(
    `[data-drawer-variant-color-selector][data-product-id="${String(productId)}"]`
  );
  if (!inputs || inputs.length === 0 || containers.length === 0) return;

  const checked = Array.from(inputs).find((input) => input.checked);
  const value = checked?.value ?? '';

  containers.forEach((container) => {
    const colorInputs = container.querySelectorAll('input[type="radio"]');
    colorInputs.forEach((input) => {
      const match = input.dataset.colorValue === value.toLowerCase();
      input.checked = match;
    });
    // Update legend with selected color
    const legend = container.querySelector('.variant-option__swatch-value');
    if (legend && checked) {
      legend.textContent = checked.getAttribute('aria-label') || value;
    }
  });

  if (value) syncDrawerProductImage(productId, value);
}

function initDrawerVariantSelectors() {
  document.querySelectorAll('[data-drawer-variant-size-selector]').forEach((el) => {
    const productId = el.dataset.productId;
    if (productId) {
      syncDrawerFromMain(productId);
      // Watch for changes in main size fieldset
      watchMainSizeFieldset(productId);
    }
  });
  document.querySelectorAll('[data-drawer-variant-color-selector]').forEach((el) => {
    const productId = el.dataset.productId;
    if (productId) syncDrawerColorFromMain(productId);
  });
}

function watchMainSizeFieldset(productId) {
  const fieldset = findMainSizeFieldset(productId);
  if (!fieldset) {
    // Main fieldset doesn't exist yet, retry in 100ms
    setTimeout(() => watchMainSizeFieldset(productId), 100);
    return;
  }

  // Watch for attribute changes (disabled state changes)
  const observer = new MutationObserver(() => {
    syncDrawerFromMain(productId);
  });

  observer.observe(fieldset, {
    attributes: true,
    subtree: true,
    attributeFilter: ['disabled', 'aria-disabled', 'checked']
  });
}

document.addEventListener('change', (e) => {
  // Handle main PDP color changes so the drawer's selected-color state stays in sync
  const mainColorInput = e.target.closest(
    'variant-picker fieldset.variant-option--swatches input[type="radio"]'
  );
  if (mainColorInput) {
    const picker = mainColorInput.closest('variant-picker');
    const productId = picker?.dataset.productId;
    if (productId) syncDrawerColorFromMain(productId);
  }

  // Handle drawer color selector changes
  const colorInput = e.target.closest('[data-drawer-variant-color-selector] input[type="radio"]');
  if (colorInput && colorInput.checked) {
    const container = colorInput.closest('[data-drawer-variant-color-selector]');
    if (container) {
      const productId = container.dataset.productId;
      if (productId) {
        // Update legend with selected color name
        const legend = container.querySelector('.variant-option__swatch-value');
        if (legend) {
          legend.textContent = colorInput.getAttribute('aria-label') || colorInput.value;
        }

        // Swap the details image to the newly selected color immediately,
        // without waiting on the main PDP picker round-trip below.
        syncDrawerProductImage(productId, colorInput.dataset.colorValue);

        // Click the matching color in main PDP
        const inputs = findMainColorInputs(productId);
        if (inputs && inputs.length > 0) {
          const targetValue = colorInput.dataset.colorValue;
          const input = Array.from(inputs).find(
            (el) => el.value.toLowerCase() === targetValue
          );
          if (input && !input.checked) {
            input.click();
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
        // Sync sizes after color change
        setTimeout(() => syncDrawerFromMain(productId), 150);
      }
    }
  }
});

// Handle drawer size button clicks (buttons, not radio inputs)
document.addEventListener('click', (e) => {
  const sizeBtn = e.target.closest('[data-drawer-variant-size-selector] .drawer-variant-size-selector__btn');
  if (sizeBtn && !sizeBtn.disabled) {
    const container = sizeBtn.closest('[data-drawer-variant-size-selector]');
    if (container) {
      const productId = container.dataset.productId;
      if (productId) {
        // Update legend with selected size
        const legend = container.querySelector('.variant-option__swatch-value');
        if (legend) {
          legend.textContent = sizeBtn.dataset.sizeValue;
        }

        const fieldset = findMainSizeFieldset(productId);
        if (fieldset) {
          const targetValue = sizeBtn.dataset.sizeValue;
          const input = Array.from(fieldset.querySelectorAll('input[type="radio"]')).find(
            (el) => el.value === targetValue
          );
          if (input && !input.disabled && !input.checked) {
            input.click();
          }
        }
      }
    }
  }
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initDrawerVariantSelectors);
} else {
  initDrawerVariantSelectors();
}

document.addEventListener('shopify:section:load', initDrawerVariantSelectors);
